/**
 * AuditLogView.js — Portal Audit Log tab (owner only)
 *
 * Reads from: restaurants_data/{restaurantId}/audit_log
 * Shows a live, filterable, searchable list of all audit events.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';

// ── Label & category maps (mirrors auditLog.js constants) ────────────────────

const AUDIT_LABELS = {
  // POS actions
  manager_override:             'Manager Override',
  owner_settings_access:        'Owner Settings Accessed',
  void_item:                    'Item Voided',
  void_check:                   'Check Voided',
  comp_item:                    'Item Comped',
  comp_check:                   'Check Comped',
  refund:                       'Payment Refunded',
  partial_refund:               'Partial Refund',
  tip_adjusted:                 'Tip Adjusted',
  force_bust:                   'Force Bust',
  cash_no_sale:                 'No-Sale Drawer Open',
  cash_paid_out:                'Cash Paid Out',
  cash_paid_in:                 'Cash Paid In',
  cash_sale:                    'Cash Sale',
  shift_opened:                 'Shift Opened',
  shift_closed:                 'Shift Closed',
  drawer_opened:                'Cash Drawer Opened',
  drawer_closed:                'Cash Drawer Closed',
  clock_in:                     'Clocked In',
  clock_out:                    'Clocked Out',
  break_start:                  'Break Started',
  break_end:                    'Break Ended',
  item_created:                 'Item Created',
  item_updated:                 'Item Updated',
  item_deleted:                 'Item Deleted',
  menu_created:                 'Menu Created',
  menu_updated:                 'Menu Updated',
  menu_deleted:                 'Menu Deleted',
  // Portal settings actions
  general_settings_saved:       'Portal: General Settings',
  pickup_settings_saved:        'Portal: Pickup Settings',
  messaging_settings_saved:     'Portal: Messaging Settings',
  stripe_connected:             'Portal: Stripe Connected',
  courses_saved:                'Portal: Courses Saved',
  enforcement_saved:            'Portal: Enforcement Settings',
  // Portal staff actions
  employee_created:             'Employee Created',
  employee_updated:             'Employee Updated',
  employee_deleted:             'Employee Terminated',
  employee_permissions_changed: 'Permissions Changed',
  // Portal reservations / schedule
  reservation_created:          'Reservation Created',
  reservation_updated:          'Reservation Updated',
  reservation_cancelled:        'Reservation Cancelled',
  schedule_published:           'Schedule Published',
  // Portal menu
  menu_item_created:            'Menu Item Created',
  menu_item_updated:            'Menu Item Updated',
  menu_item_deleted:            'Menu Item Deleted',
};

const CATEGORY_CONFIG = {
  financial:    { label: 'Financial', badge: 'bg-red-100 text-red-700 border border-red-200'          },
  cash:         { label: 'Cash',      badge: 'bg-emerald-100 text-emerald-700 border border-emerald-200' },
  clock:        { label: 'Clock',     badge: 'bg-blue-100 text-blue-700 border border-blue-200'        },
  menu:         { label: 'Menu',      badge: 'bg-purple-100 text-purple-700 border border-purple-200'  },
  access:       { label: 'Access',    badge: 'bg-orange-100 text-orange-700 border border-orange-200'  },
  settings:     { label: 'Settings',  badge: 'bg-yellow-100 text-yellow-700 border border-yellow-200'  },
  staff:        { label: 'Staff',     badge: 'bg-teal-100 text-teal-700 border border-teal-200'        },
  reservations: { label: 'Reservations', badge: 'bg-sky-100 text-sky-700 border border-sky-200'       },
  other:        { label: 'Other',     badge: 'bg-neutral-100 text-neutral-600 border border-neutral-200' },
};

const ROLE_BADGE = {
  owner:    'bg-amber-100 text-amber-700',
  manager:  'bg-blue-100 text-blue-700',
  employee: 'bg-neutral-100 text-neutral-600',
};

// ── Date preset helpers ───────────────────────────────────────────────────────

const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const endOfDay   = (d) => { const x = new Date(d); x.setHours(23, 59, 59, 999); return x; };

const DATE_PRESETS = [
  { id: 'today',   label: 'Today'      },
  { id: 'yesterday', label: 'Yesterday' },
  { id: '7d',      label: 'Last 7 Days' },
  { id: '30d',     label: 'Last 30 Days' },
  { id: 'all',     label: 'All Time'   },
];

const getPresetRange = (preset) => {
  const now  = new Date();
  const today = startOfDay(now);
  switch (preset) {
    case 'today':
      return { from: today, to: endOfDay(now) };
    case 'yesterday': {
      const yd = new Date(today); yd.setDate(yd.getDate() - 1);
      return { from: startOfDay(yd), to: endOfDay(yd) };
    }
    case '7d': {
      const d = new Date(today); d.setDate(d.getDate() - 6);
      return { from: d, to: endOfDay(now) };
    }
    case '30d': {
      const d = new Date(today); d.setDate(d.getDate() - 29);
      return { from: d, to: endOfDay(now) };
    }
    default:
      return null; // all time
  }
};

// ── Timestamp formatter ───────────────────────────────────────────────────────

const formatTs = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
};

// ── Details one-liner ─────────────────────────────────────────────────────────

const buildDetailLine = (event) => {
  const parts = [];

  // Portal-specific: staff target
  if (event.targetEmployeeName) parts.push(`Target: ${event.targetEmployeeName}`);

  // Portal-specific: settings field
  if (event.field && event.category === 'settings') parts.push(`Field: ${event.field}`);

  // Portal-specific: messaging template
  if (event.templateType) parts.push(`Template: ${event.templateType}`);

  // Reservation cancellation
  if (event.guestName)  parts.push(event.guestName);
  if (event.guestPhone) parts.push(event.guestPhone);
  if (event.reservationDate) parts.push(event.reservationDate);
  if (event.reservationTime) parts.push(event.reservationTime);

  // POS actions
  if (event.itemName)  parts.push(`Item: ${event.itemName}`);
  if (event.menuName)  parts.push(`Menu: ${event.menuName}`);
  if (event.price != null) parts.push(`$${Number(event.price).toFixed(2)}`);
  if (event.breakType) parts.push(`Type: ${event.breakType}`);
  if (event.durationMinutes != null) parts.push(`${event.durationMinutes} min`);
  if (event.screen)    parts.push(`Screen: ${event.screen}`);
  if (event.timesheetId && !parts.length) parts.push(`Shift: ${event.timesheetId.slice(0, 8)}…`);

  return parts.join(' · ') || null;
};

// ── Skeleton loader ───────────────────────────────────────────────────────────

const SkeletonRow = () => (
  <div className="flex items-center gap-3 px-4 py-3 border-b border-neutral-100 animate-pulse">
    <div className="w-28 h-4 bg-neutral-200 rounded" />
    <div className="flex-1 h-4 bg-neutral-200 rounded" />
    <div className="w-20 h-5 bg-neutral-200 rounded-full" />
    <div className="w-16 h-5 bg-neutral-200 rounded-full" />
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────

const AuditLogView = ({ restaurantId }) => {
  const [events,        setEvents]        = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [datePreset,    setDatePreset]    = useState('7d');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [search,        setSearch]        = useState('');

  // Live Firestore subscription — latest 200 events
  useEffect(() => {
    if (!restaurantId) return;
    setLoading(true);
    const q = query(
      collection(db, 'restaurants_data', restaurantId, 'audit_log'),
      orderBy('timestamp', 'desc'),
      limit(200),
    );
    const unsub = onSnapshot(q, (snap) => {
      setEvents(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (err) => {
      console.error('[AuditLogView] snapshot error:', err);
      setLoading(false);
    });
    return () => unsub();
  }, [restaurantId]);

  // Client-side filtering
  const filtered = useMemo(() => {
    const range       = getPresetRange(datePreset);
    const searchLower = search.trim().toLowerCase();

    return events.filter(ev => {
      // Date range
      if (range) {
        const ts = ev.timestamp?.toDate ? ev.timestamp.toDate() : null;
        if (!ts || ts < range.from || ts > range.to) return false;
      }

      // Category
      if (categoryFilter !== 'all' && ev.category !== categoryFilter) return false;

      // Employee name search
      if (searchLower && !(ev.employeeName || '').toLowerCase().includes(searchLower)) return false;

      return true;
    });
  }, [events, datePreset, categoryFilter, search]);

  return (
    <div className="flex flex-col gap-4">

      {/* Page header */}
      <div className="shrink-0">
        <h2 className="text-xl font-bold text-neutral-800">Audit Log</h2>
        <p className="text-sm text-neutral-500 mt-0.5">Track who changed what</p>
      </div>

      {/* Filter bar */}
      <div className="shrink-0 flex flex-wrap gap-3 items-start">

        {/* Date preset chips */}
        <div className="flex flex-wrap gap-1.5">
          {DATE_PRESETS.map(p => (
            <button
              key={p.id}
              onClick={() => setDatePreset(p.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                datePreset === p.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Category chips */}
        <div className="flex flex-wrap gap-1.5">
          <button
            onClick={() => setCategoryFilter('all')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
              categoryFilter === 'all'
                ? 'bg-neutral-800 text-white'
                : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
            }`}
          >
            All
          </button>
          {Object.entries(CATEGORY_CONFIG).filter(([k]) => k !== 'other').map(([key, cfg]) => (
            <button
              key={key}
              onClick={() => setCategoryFilter(key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                categoryFilter === key
                  ? 'bg-neutral-800 text-white'
                  : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
              }`}
            >
              {cfg.label}
            </button>
          ))}
        </div>

        {/* Search box */}
        <input
          type="text"
          placeholder="Search by employee name…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="border border-neutral-200 rounded-lg px-3 py-1.5 text-sm text-neutral-800 placeholder-neutral-400 focus:outline-none focus:border-blue-400 min-w-[200px]"
        />
      </div>

      {/* Event list */}
      <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">

        {/* Table header — hidden on small screens */}
        <div className="hidden sm:grid grid-cols-[160px_1fr_120px_90px] gap-4 px-4 py-2.5 bg-neutral-50 border-b border-neutral-200 text-xs font-semibold text-neutral-500 uppercase tracking-wide">
          <span>Time</span>
          <span>Employee / Action / Details</span>
          <span>Action</span>
          <span>Category</span>
        </div>

        {loading ? (
          <>
            <SkeletonRow /><SkeletonRow /><SkeletonRow /><SkeletonRow /><SkeletonRow />
          </>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
            <p className="text-2xl mb-2">🔍</p>
            <p className="text-sm font-medium text-neutral-500">No audit events found</p>
            <p className="text-xs text-neutral-400 mt-1">
              Try adjusting the date range, category filter, or search term.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-neutral-100 max-h-[60vh] overflow-y-auto">
            {filtered.map(ev => {
              const catCfg    = CATEGORY_CONFIG[ev.category] || CATEGORY_CONFIG.other;
              const roleCls   = ROLE_BADGE[ev.employeeRole] || ROLE_BADGE.employee;
              const label     = AUDIT_LABELS[ev.action] || ev.action;
              const detail    = buildDetailLine(ev);

              return (
                <div
                  key={ev.id}
                  className="flex flex-col sm:grid sm:grid-cols-[160px_1fr_120px_90px] sm:gap-4 px-4 py-3 hover:bg-neutral-50 transition-colors text-sm"
                >
                  {/* Timestamp */}
                  <span className="text-xs text-neutral-400 shrink-0 mb-1 sm:mb-0 sm:self-center">
                    {formatTs(ev.timestamp)}
                  </span>

                  {/* Employee + detail */}
                  <div className="flex flex-col gap-0.5 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-neutral-800 truncate">
                        {ev.employeeName || 'Unknown'}
                      </span>
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${roleCls}`}>
                        {ev.employeeRole || 'staff'}
                      </span>
                    </div>
                    {detail && (
                      <span className="text-xs text-neutral-400 truncate">{detail}</span>
                    )}
                    {/* Mobile: show action + category inline */}
                    <div className="flex gap-2 mt-1 sm:hidden flex-wrap">
                      <span className="text-xs text-neutral-700 font-medium">{label}</span>
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${catCfg.badge}`}>
                        {catCfg.label}
                      </span>
                    </div>
                  </div>

                  {/* Action label — desktop only */}
                  <span className="hidden sm:block text-xs text-neutral-700 font-medium self-center truncate">
                    {label}
                  </span>

                  {/* Category badge — desktop only */}
                  <div className="hidden sm:flex items-center">
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${catCfg.badge}`}>
                      {catCfg.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Footer count */}
        {!loading && filtered.length > 0 && (
          <div className="px-4 py-2.5 border-t border-neutral-100 bg-neutral-50">
            <p className="text-xs text-neutral-400">
              Showing {filtered.length} event{filtered.length !== 1 ? 's' : ''}
              {filtered.length === 200 ? ' (limit 200 — use date filters to narrow)' : ''}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditLogView;
